/* eslint-disable camelcase */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import Select from 'react-select';
import { useCookies } from 'react-cookie';
import { Formik, FormikActions } from 'formik';

import SubmitButton from '../../ui/SubmitButton';
import Alert from '../../ui/Alert';
import FormControl from '../../ui/FormControl';
import ControlLabel from '../../ui/ControlLabel';
import FormGroup from '../../ui/FormGroup';
import { jobFormSelectStyles } from '../../ui/sharedStyles';
import Tooltip from '../../ui/Tooltip';
import SubmissionErrors from '../../shared/SubmissionErrors';
import ExampleFilesGuide from '../../shared/ExampleFilesGuide';
import {
  DEEPSEA_ANALYSES,
  ANALYSES_META_INFO,
  DEEPSEA_MODELS,
  ANALYSIS_CHARACTERISTICS,
  DEFAULT_ANALYSIS,
  GENOME_ASSEMBLY_COOKIE,
  MAX_COOKIE_AGE,
  AnalysisCharacteristics,
  STATIC_HOST_URL,
} from '../../../settings';
import {
  fetchNewDeepSEAJob,
  setDeepSEAJobValidationErrors,
} from '../../../actions/DeepSEAActions';
import { readFileText } from '../../../core/util';

// Keep display order independent of the selected analysis's allowed-format list.
const EXAMPLE_FORMATS = [
  {
    format: 'VCF',
    purpose: 'Variant effect prediction',
    summary:
      'Tab-separated, at least 5 columns: chromosome, position, ID, reference and alternative allele.',
    files: [
      {
        name: 'example.vcf',
        href: `${STATIC_HOST_URL}deepsea/examples/example.vcf`,
      },
      {
        name: 'example_strand.vcf',
        href: `${STATIC_HOST_URL}deepsea/examples/example_strand.vcf`,
      },
    ],
    rules: (
      <ul>
        <li>
          A header line, if present, starts with <code>#</code> and its first 5
          fields are <code>#CHROM</code>, <code>POS</code>, <code>ID</code>,{' '}
          <code>REF</code>, <code>ALT</code>.
        </li>
        <li>
          Position is a positive integer within the chromosome&apos;s length.
        </li>
        <li>The ID column can be left empty.</li>
        <li>
          Reference and alternative alleles use upper-case ATCG, up to 100
          characters.
        </li>
        <li>
          <strong>Seqweaver</strong> also needs a sixth column: strand,{' '}
          <code>+</code> or <code>-</code>.
        </li>
        <li>VCF files open in any text editor or spreadsheet program.</li>
      </ul>
    ),
  },
  {
    format: 'BED',
    purpose: 'Chromatin feature prediction',
    summary:
      'Tab-separated, at least 3 columns: chromosome, start and end. Each interval is scored at its center.',
    files: [
      {
        name: 'example.bed',
        href: `${STATIC_HOST_URL}deepsea/examples/example.bed`,
      },
    ],
    rules: (
      <ul>
        <li>
          Start and end are non-negative integers within the chromosome&apos;s
          length.
        </li>
        <li>
          For an even-length interval the left-center position is used:{' '}
          <code>chr1 100 106</code> is scored at 103.
        </li>
      </ul>
    ),
  },
  {
    format: 'FASTA',
    purpose: 'Chromatin feature prediction or in-silico mutagenesis',
    summary: (
      <>
        Each sequence starts with <code>&gt;</code>. Length depends on the
        model: Sei 4096 bp, Beluga 2000 bp, Seqweaver 1000 bp.
      </>
    ),
    files: [
      {
        name: 'example.fasta',
        href: `${STATIC_HOST_URL}deepsea/examples/example.fasta`,
      },
    ],
    rules: (
      <ul>
        <li>Allowed codes are ATCG; lower case is converted to upper case.</li>
      </ul>
    ),
  },
];

// A utility function to retrieve the appropriate example text based on format, genome, and analysis.
const getExampleByGenomeAndFormat = (
  format: string,
  genome = 'hg19',
  analysis: string | null = null,
  isStranded = false,
): string => {
  const genomeVersion = genome || 'hg19';
  const analysisConfig: AnalysisCharacteristics =
    ANALYSIS_CHARACTERISTICS[analysis || ''] || DEFAULT_ANALYSIS;
  const needsStranded = isStranded || analysisConfig.requiresStranded;

  if (format === 'fasta') {
    return analysisConfig.fastaExample;
  }

  if (format === 'vcf') {
    const formatType: 'stranded' | 'regular' = needsStranded
      ? 'stranded'
      : 'regular';
    return analysisConfig.vcfExamples[genomeVersion as 'hg19' | 'hg38'][
      formatType
    ];
  }

  if (format === 'bed') {
    const formatType: 'stranded' | 'regular' = needsStranded
      ? 'stranded'
      : 'regular';
    return analysisConfig.bedExamples[genomeVersion as 'hg19' | 'hg38'][
      formatType
    ];
  }

  return '';
};

interface JobValidationError {
  [key: string]: string[] | string;
}

interface DeepSEAFormProps {
  analysis: string;
  prefill?: {
    textarea: string;
    title: string;
  } | null;
  defaultModelName: string;
  modelName?: string | null;
}

interface FormValues {
  title: string;
  email: string;
  input_type: string;
  ds_model: string;
  signup: boolean;
  expecto: boolean;
  upload_textarea: string;
  upload_file: File | null;
  insilico: boolean;
  genome_assembly: string;
  upload_radio?: string;
}

interface ModelOption {
  value: string;
  name: string;
  stranded: boolean;
  vizInputTypes: string[];
  label: string;
}

interface FormatOption {
  value: string;
  label: string;
}

interface GenomeOption {
  value: string;
  label: string;
}

interface DeepSeaJob {
  job_id: string;
  input_type: string;
  insilico: boolean;
  ds_model: string;
  created: string;
  [key: string]: any;
}

interface RootState {
  deepsea: {
    jobValidationErrors: JobValidationError;
    selectedJob: DeepSeaJob;
    jobs: {
      [key: string]: DeepSeaJob;
    };
  };
}

// A compact box under the input.
const lengthAlertStyle: React.CSSProperties = {
  padding: '10px 12px',
  marginTop: 10,
  marginBottom: 0,
};

// Long FASTA headers wrap anywhere instead of widening the box.
const lengthListStyle: React.CSSProperties = {
  margin: '6px 0 0',
  paddingLeft: 20,
  overflowWrap: 'anywhere',
};

const MAX_SEQUENCES_SHOWN = 5;

const bp = (n: number) => `${n.toLocaleString('en-US')} bp`;

export interface LengthMismatch {
  name: string;
  length: number;
}

// Records the server will pad or trim to `expected` bp, in file order.
export function fastaLengthMismatches(
  text: string,
  expected: number,
): LengthMismatch[] {
  // Text before the first '>' header is not a record.
  return text
    .split(/^>/m)
    .slice(1)
    .map(function measure(record) {
      const [header, ...lines] = record.split('\n');
      // Line breaks and spaces are not sequence.
      const { length } = lines.join('').replace(/\s/g, '');
      return { name: header.trim(), length };
    })
    .filter(record => record.length !== expected);
}

// FASTA records the selected model will pad or cut.
function lengthMismatchesFor(
  values: FormValues,
  text: string | null,
): LengthMismatch[] {
  const expected = ANALYSIS_CHARACTERISTICS[values.ds_model]?.sequenceLength;
  if (values.input_type !== 'fasta' || !text || !expected) {
    return [];
  }
  return fastaLengthMismatches(text, expected);
}

function LengthWarningText({
  values,
  mismatches,
}: {
  values: FormValues;
  mismatches: LengthMismatch[];
}) {
  const expected = ANALYSIS_CHARACTERISTICS[values.ds_model].sequenceLength;
  const model =
    DEEPSEA_MODELS.find(m => m.name.toLowerCase() === values.ds_model)?.name ||
    values.ds_model;
  const hidden = mismatches.length - MAX_SEQUENCES_SHOWN;
  // Only padding can affect predictions; trimming keeps the scored window.
  const hasShort = mismatches.some(m => m.length < expected);
  const hasLong = mismatches.some(m => m.length > expected);
  return (
    <>
      <strong>{`Some sequences are not exactly ${bp(expected)}.`}</strong>
      {hasLong &&
        ` Longer sequences are trimmed to the central ${bp(
          expected,
        )}, since that is all ${model} needs.`}
      {hasShort &&
        ' Shorter sequences are padded with N on both sides, which can affect the predictions.'}
      <ul style={lengthListStyle}>
        {mismatches.slice(0, MAX_SEQUENCES_SHOWN).map(m => (
          <li key={`${m.name}:${m.length}`}>
            {m.name}{' '}
            <span style={{ whiteSpace: 'nowrap', opacity: 0.8 }}>
              {bp(m.length)}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 && <div>{`… and ${hidden} more`}</div>}
      {values.insilico && (
        <div style={{ marginTop: 6 }}>
          Mutagenesis positions will follow the adjusted sequence.
        </div>
      )}
    </>
  );
}

// A function to perform client-side form validation.
function validateForm(values: FormValues): JobValidationError {
  const errors: JobValidationError = {};

  // Validate that at least one upload method has content. Set on
  // both keys so the error renders right below whichever input the
  // user is on — the file and textarea inputs are mutually
  // unmounted via the upload_radio tab switch, so only the active
  // tab's error display is in the DOM at any moment.
  if (!values.upload_file && !values.upload_textarea) {
    const msg = ['Upload file or pasted text is required.'];
    errors.upload_file = msg;
    errors.upload_textarea = msg;
  }

  // Validate the uploaded file properties if a file is selected.
  if (values.upload_file) {
    if (values.upload_file.size === 0) {
      errors.upload_file = ['Uploaded file is empty'];
    } else if (values.upload_file.name.split('.').length === 1) {
      errors.upload_file = ['Please use file extension'];
    }
  }

  return errors;
}

const DeepSEAForm: React.FC<DeepSEAFormProps> = ({
  prefill,
  analysis,
  defaultModelName,
  modelName = null,
}) => {
  // ThunkDispatch typing so `await dispatch(fetchNewDeepSEAJob(...))`
  // resolves to the thunk's return value (job_id | undefined) rather
  // than the default Dispatch<AnyAction> which loses thunk semantics.
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const [cookies, setCookie] = useCookies([GENOME_ASSEMBLY_COOKIE]);

  // Selectors to get data from the Redux store.
  const { jobValidationErrors } = useSelector<
    RootState,
    { jobValidationErrors: JobValidationError }
  >(state => state.deepsea);

  // Bumped on every successful submission. Used as a remount key on
  // the native <input type="file"> so its DOM-side selection clears
  // after a job is created. resetForm() sets values.upload_file=null
  // in Formik, but the file input is uncontrolled (its `value` can't
  // be set programmatically by React), so without a key bump the
  // visual filename lingers — and toggling key based on
  // truthy/falsy values.upload_file would unmount the input the
  // instant the user picked a file, dropping the selection.
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  // The picked file's text, for the length warning.
  const [fileText, setFileText] = useState<string | null>(null);

  // Bumped per pick, so a late read cannot show a stale warning.
  const fileReadCount = useRef(0);
  const forgetFileText = () => {
    fileReadCount.current += 1;
    setFileText(null);
  };

  // Memoized options and configurations to prevent unnecessary re-creation on re-renders.
  const modelOptions = useMemo(
    () =>
      DEEPSEA_MODELS.map(model => ({
        value: model.name.toLowerCase(),
        ...model,
      })),
    [],
  );

  const formatOptions = useMemo(
    () => [
      { value: 'vcf', label: 'VCF' },
      { value: 'bed', label: 'BED' },
      { value: 'fasta', label: 'FASTA' },
    ],
    [],
  );

  const genomeOptions = useMemo(
    () => [
      { value: 'hg19', label: 'GRCh37/hg19' },
      { value: 'hg38', label: 'GRCh38/hg38' },
    ],
    [],
  );

  const analysisConfig = useMemo(() => ANALYSES_META_INFO[analysis], [
    analysis,
  ]);

  const defaultGenomeOption = useMemo(
    () =>
      genomeOptions.find(
        genome => genome.value === cookies[GENOME_ASSEMBLY_COOKIE],
      ) ||
      genomeOptions.find(
        genome => genome.value === analysisConfig?.genomes?.[0],
      ) ||
      genomeOptions[0],
    [cookies, genomeOptions, analysisConfig],
  );

  // Keep errors beside their inputs so inactive upload tabs stay hidden.
  const getErrors = useCallback(
    (fieldName: string) => {
      const errors = jobValidationErrors[fieldName];
      return errors ? (
        <SubmissionErrors errors={{ [fieldName]: errors }} inline />
      ) : null;
    },
    [jobValidationErrors],
  );

  // The form is keyed on the analysis, so each analysis mounts it afresh:
  // drop errors left by another analysis. The job selector owns which job
  // is selected, including the one restored after a reload.
  useEffect(() => {
    dispatch(setDeepSEAJobValidationErrors({}));
  }, [dispatch]);

  const formOnChanged = useCallback(() => {
    // Skip the dispatch when there's nothing to clear: the reducer
    // always returns a fresh state object, so an unconditional
    // dispatch on every keystroke forces a useSelector re-render of
    // the whole form for no observable change.
    if (Object.keys(jobValidationErrors).length > 0) {
      dispatch(setDeepSEAJobValidationErrors({}));
    }
  }, [dispatch, jobValidationErrors]);

  const uploadFormatHelpText = useMemo(() => {
    if (analysis === DEEPSEA_ANALYSES.ism) {
      return 'Upload or paste using the FASTA format (DNA sequence).';
    }
    if (analysis === DEEPSEA_ANALYSES.expecto) {
      return 'Upload or paste using the VCF format.';
    }
    return 'To predict variant effects, upload or paste VCF formats. To predict DNA sequence features, upload or paste FASTA formats (DNA sequence) or BED formats (genomic region in hg19 coordinates).';
  }, [analysis]);

  const analysisText = useMemo(
    () =>
      analysis === DEEPSEA_ANALYSES.ism
        ? 'an in silico mutagenesis'
        : analysis === DEEPSEA_ANALYSES.expecto
        ? `an ${analysisConfig.name}`
        : `a ${analysisConfig.name}`,
    [analysis, analysisConfig],
  );

  const modelHelpText =
    'Select the sequence model for predicting transcriptional (DNA) or post-transcriptional (RNA) effects.';

  const initialInputType = analysisConfig?.fileFormats?.[0] || '';
  const formatDescriptionList = analysisConfig?.fileFormats || [];
  const analysisURL = analysisConfig?.helpUrl || '';

  const submitJob = async (
    values: FormValues,
    { setSubmitting, resetForm }: FormikActions<FormValues>,
  ) => {
    // Formik 1.x never clears isSubmitting itself; the finally below does.
    try {
      const fieldErrors = validateForm(values);

      if (Object.entries(fieldErrors).length > 0) {
        dispatch(setDeepSEAJobValidationErrors(fieldErrors));
      } else {
        dispatch(setDeepSEAJobValidationErrors({}));
        // The thunk is untyped JS: it returns the job_id, or undefined.
        const newJobId = (await (dispatch(
          fetchNewDeepSEAJob(values),
        ) as unknown)) as string | undefined;
        if (newJobId) {
          // Keep the user's settings; plain resetForm() would reset them.
          resetForm({
            ...values,
            title: '',
            upload_file: null,
            upload_textarea: '',
          });
          // Remount the file input to clear its filename.
          setFileInputResetKey(k => k + 1);
          forgetFileText();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h3 style={{ marginBottom: '40px' }}>
        {`Submit ${analysisText} analysis `}
        <Tooltip
          id="helpLink"
          text={`Help for ${analysisConfig.name}`}
          url={analysisURL}
        />
      </h3>
      <Formik
        initialStatus={{
          upload_radio: prefill ? 'textarea' : 'file',
        }}
        initialValues={{
          title: prefill?.title || '',
          email: '',
          input_type: initialInputType,
          ds_model: modelName || defaultModelName,
          signup: false,
          expecto: analysis === DEEPSEA_ANALYSES.expecto,
          upload_textarea: prefill?.textarea || '',
          upload_file: null,
          insilico: analysis === DEEPSEA_ANALYSES.ism,
          genome_assembly: defaultGenomeOption.value,
        }}
        onSubmit={submitJob}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          status,
          setStatus,
          isSubmitting,
        }) => {
          const formStatus = status as {
            upload_radio: string;
          };

          const handleUploadFileChange = (
            e: React.ChangeEvent<HTMLInputElement>,
          ) => {
            const file = e.currentTarget.files?.[0];
            if (file) {
              const fileName = file.name.split('.');
              // Normalize extension casing so data.VCF / data.BED /
              // data.FASTA auto-update the input_type dropdown the
              // same way data.vcf / data.bed / data.fasta do.
              const fileType = fileName.pop()?.toLowerCase();
              if (
                fileType === 'vcf' ||
                fileType === 'bed' ||
                fileType === 'fasta'
              ) {
                setFieldValue('input_type', fileType);
              }
            }
            setFieldValue('upload_file', file || null);
            // Read the pick for the length warning.
            forgetFileText();
            const readNumber = fileReadCount.current;
            if (file) {
              readFileText(file)
                .then(text => {
                  if (readNumber === fileReadCount.current) {
                    setFileText(text);
                  }
                })
                .catch(() => {
                  // Unreadable here; the server still validates the file.
                });
            }
            formOnChanged();
          };

          // The warning follows whichever input is showing.
          const lengthMismatches = lengthMismatchesFor(
            values,
            formStatus.upload_radio === 'file'
              ? fileText
              : values.upload_textarea,
          );
          // Always rendered, so screen readers announce the warning.
          const lengthWarning = (
            <div role="status">
              {lengthMismatches.length > 0 && (
                <Alert bsStyle="warning" role="none" style={lengthAlertStyle}>
                  <i
                    className="fa fa-exclamation-triangle"
                    aria-hidden="true"
                    style={{ marginRight: 6 }}
                  />
                  <LengthWarningText
                    values={values}
                    mismatches={lengthMismatches}
                  />
                </Alert>
              )}
            </div>
          );

          const handleTextareaChange = (
            e: React.ChangeEvent<HTMLTextAreaElement>,
          ) => {
            setFieldValue('upload_textarea', e.currentTarget.value);
            formOnChanged();
          };

          const handleModelChange = (e: ModelOption | null) => {
            if (!e) return;
            setFieldValue('ds_model', e.value);
            const model = modelOptions.find(m => m.value === e.value);
            const isStranded = model?.stranded || false;

            if (formStatus.upload_radio === 'example') {
              setFieldValue(
                'upload_textarea',
                getExampleByGenomeAndFormat(
                  values.input_type,
                  values.genome_assembly,
                  analysis,
                  isStranded,
                ),
              );
            }
            formOnChanged();
          };

          const handleInputTypeChange = (e: FormatOption | null) => {
            if (!e) return;
            setFieldValue('input_type', e.value);
            const model = modelOptions.find(m => m.value === values.ds_model);
            const isStranded = model?.stranded || false;

            if (formStatus.upload_radio === 'example') {
              setFieldValue(
                'upload_textarea',
                getExampleByGenomeAndFormat(
                  e.value,
                  values.genome_assembly,
                  analysis,
                  isStranded,
                ),
              );
            }
            formOnChanged();
          };

          const handleGenomeChange = (e: GenomeOption | null) => {
            if (!e) return;
            setFieldValue('genome_assembly', e.value);
            setCookie(GENOME_ASSEMBLY_COOKIE, e.value, {
              path: window.location.pathname,
              maxAge: MAX_COOKIE_AGE,
            });

            // Update example text if the example radio button is selected.
            if (formStatus.upload_radio === 'example') {
              const model = modelOptions.find(m => m.value === values.ds_model);
              const isStranded = model?.stranded || false;

              setFieldValue(
                'upload_textarea',
                getExampleByGenomeAndFormat(
                  values.input_type,
                  e.value,
                  analysis,
                  isStranded,
                ),
              );
            }
            formOnChanged();
          };

          const fileOrPaste =
            formStatus.upload_radio === 'file' ? (
              <FormGroup
                controlId="formDeepSEAFile"
                validationState={
                  jobValidationErrors.upload_file ? 'error' : null
                }
              >
                <ControlLabel>Select an upload file</ControlLabel>
                &nbsp;
                <Tooltip text={uploadFormatHelpText}>
                  <i className="fa fa-question-circle" />
                </Tooltip>
                <FormControl
                  key={`upload_file_${fileInputResetKey}`}
                  id="formDeepSEAFile"
                  name="upload_file"
                  type="file"
                  onChange={handleUploadFileChange}
                  disabled={isSubmitting}
                />
                {getErrors('upload_file')}
                {lengthWarning}
              </FormGroup>
            ) : (
              <FormGroup
                controlId="formDeepSEATextarea"
                validationState={
                  jobValidationErrors.upload_textarea ? 'error' : null
                }
              >
                <ControlLabel>Submission</ControlLabel>
                &nbsp;
                <Tooltip text={uploadFormatHelpText}>
                  <i className="fa fa-question-circle" />
                </Tooltip>
                <FormControl
                  componentClass="textarea"
                  id="formDeepSEATextarea"
                  name="upload_textarea"
                  disabled={isSubmitting}
                  style={{
                    height: 'auto',
                    minHeight: '100px',
                    resize: 'vertical',
                  }}
                  value={values.upload_textarea}
                  onChange={handleTextareaChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    // Prevent Undo (Cmd/Ctrl-Z): the textarea inserts
                    // literal tab characters from the Tab key below,
                    // and the browser-level undo stack can't replay
                    // that cleanly. Use the synchronous event
                    // properties (e.metaKey covers macOS Cmd,
                    // e.ctrlKey covers Windows/Linux Ctrl) instead of
                    // tracking modifier state through async
                    // setStatus, which races on fast keystrokes.
                    // Lowercase the key so Caps Lock or Shift-Z
                    // doesn't bypass the check (e.key reflects the
                    // composed glyph, so Z != z under Caps Lock).
                    // Catch 'y' as well so Windows/Linux Ctrl-Y redo
                    // doesn't fight with the tab-insertion logic.
                    if (
                      (e.metaKey || e.ctrlKey) &&
                      ['z', 'y'].includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault();
                    }

                    // Prevent default tab behavior and instead insert a tab character.
                    if (['Tab'].includes(e.key)) {
                      e.preventDefault();
                      const { selectionStart, selectionEnd } = e.currentTarget;
                      const { value } = e.currentTarget;
                      const newValue = `${value.substring(
                        0,
                        selectionStart,
                      )}\t${value.substring(selectionEnd)}`;
                      e.currentTarget.value = newValue;
                      // Place the caret immediately AFTER the
                      // inserted tab. Without this, writing to .value
                      // collapses the selection to the end of the
                      // textarea — the user has to re-find their
                      // place every Tab keystroke.
                      e.currentTarget.selectionStart = selectionStart + 1;
                      e.currentTarget.selectionEnd = selectionStart + 1;
                      setFieldValue('upload_textarea', newValue);
                      // Treat Tab-insertion like a normal edit so any
                      // visible validation errors clear, the same way
                      // handleTextareaChange does for typed input.
                      formOnChanged();
                    }
                  }}
                />
                {getErrors('upload_textarea')}
                {lengthWarning}
              </FormGroup>
            );

          const radioOptions = [
            {
              id: 'upload-radio-file',
              value: 'file',
              label: 'File upload',
              onChange: () => {
                setStatus({ ...status, upload_radio: 'file' });
                setFieldValue('upload_textarea', '');
                formOnChanged();
              },
            },
            {
              id: 'upload-radio-textarea',
              value: 'textarea',
              label: 'Paste contents',
              onChange: () => {
                setStatus({ ...status, upload_radio: 'textarea' });
                setFieldValue('upload_file', null);
                forgetFileText();
                formOnChanged();
              },
            },
            {
              id: 'upload-radio-example',
              value: 'example',
              label: `Example ${values.input_type.toUpperCase()} submission`,
              onChange: () => {
                setStatus({ ...status, upload_radio: 'example' });
                setFieldValue('upload_file', null);
                forgetFileText();
                const model = modelOptions.find(
                  m => m.value === values.ds_model,
                );
                const isStranded = model?.stranded || false;

                setFieldValue(
                  'upload_textarea',
                  getExampleByGenomeAndFormat(
                    values.input_type,
                    values.genome_assembly,
                    analysis,
                    isStranded,
                  ),
                );
                formOnChanged();
              },
            },
          ];

          return (
            <form encType="multipart/form-data" onSubmit={handleSubmit}>
              <FormGroup
                controlId="formDeepSEAModel"
                validationState={jobValidationErrors.ds_model ? 'error' : null}
              >
                <ControlLabel>Model</ControlLabel>
                &nbsp;
                <Tooltip text={modelHelpText}>
                  <i className="fa fa-question-circle" />
                </Tooltip>
                <div style={{ marginBottom: '5px' }}>
                  <Select<ModelOption>
                    inputId="formDeepSEAModel"
                    name="ds_model"
                    isSearchable={false}
                    options={modelOptions}
                    styles={jobFormSelectStyles}
                    onChange={handleModelChange}
                    value={
                      modelOptions.find(
                        model => model.value === values.ds_model,
                      ) || null
                    }
                    isDisabled={
                      values.insilico ||
                      values.expecto ||
                      !!modelName ||
                      isSubmitting
                    }
                  />
                </div>
                {getErrors('ds_model')}
              </FormGroup>

              <FormGroup
                controlId="formDeepSEAType"
                validationState={
                  jobValidationErrors.input_type ? 'error' : null
                }
                style={{
                  display:
                    (analysisConfig?.fileFormats?.length ?? 0) > 1
                      ? 'block'
                      : 'none',
                }}
              >
                <ControlLabel>Type</ControlLabel>
                <div style={{ marginBottom: '5px' }}>
                  <Select<FormatOption>
                    inputId="formDeepSEAType"
                    name="input_type"
                    isSearchable={false}
                    isDisabled={isSubmitting}
                    options={formatOptions}
                    styles={jobFormSelectStyles}
                    onChange={handleInputTypeChange}
                    value={
                      formatOptions.find(
                        format => format.value === values.input_type,
                      ) || null
                    }
                  />
                </div>
                {getErrors('input_type')}
              </FormGroup>

              <FormGroup
                controlId="formDeepSEAGenome"
                validationState={
                  jobValidationErrors.genome_assembly ? 'error' : null
                }
                style={{
                  display:
                    analysisConfig.genomes && values.input_type !== 'fasta'
                      ? 'block'
                      : 'none',
                }}
              >
                <ControlLabel>Genome assembly</ControlLabel>
                <div style={{ marginBottom: '5px' }}>
                  <Select<GenomeOption>
                    inputId="formDeepSEAGenome"
                    name="genome_assembly"
                    isSearchable={false}
                    isDisabled={
                      analysisConfig.genomes?.length === 1 || isSubmitting
                    }
                    options={genomeOptions}
                    styles={jobFormSelectStyles}
                    onChange={handleGenomeChange}
                    value={
                      genomeOptions.find(
                        genome => genome.value === values.genome_assembly,
                      ) || null
                    }
                  />
                </div>
                {getErrors('genome_assembly')}
              </FormGroup>

              <ExampleFilesGuide
                formats={EXAMPLE_FORMATS.filter(row =>
                  formatDescriptionList.includes(row.format.toLowerCase()),
                )}
              />

              <FormGroup>
                <div
                  id="upload-options-group"
                  style={{ fontWeight: 'bold', marginBottom: 5 }}
                >
                  Upload options
                </div>
                <div role="group" aria-labelledby="upload-options-group">
                  {radioOptions.map(option => (
                    // A flex row centres the radio on its label; Bootstrap's
                    // 4px top margin on radios otherwise sits it low.
                    <div
                      key={option.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginRight: '15px',
                        marginTop: '1px',
                      }}
                    >
                      <input
                        type="radio"
                        id={option.id}
                        name="upload_radio"
                        checked={formStatus.upload_radio === option.value}
                        value={option.value}
                        onChange={option.onChange}
                        disabled={isSubmitting}
                        style={{ margin: '0 5px 0 0' }}
                      />
                      <ControlLabel
                        htmlFor={option.id}
                        style={{ fontWeight: 'normal', marginBottom: '0' }}
                      >
                        {option.label}
                      </ControlLabel>
                    </div>
                  ))}
                </div>
              </FormGroup>

              {fileOrPaste}

              <FormGroup
                controlId="formDeepSEATitle"
                validationState={jobValidationErrors.title ? 'error' : null}
              >
                <ControlLabel>Job title</ControlLabel>
                &nbsp;
                <i>(optional)</i>
                <FormControl
                  id="formDeepSEATitle"
                  name="title"
                  type="text"
                  placeholder="Untitled"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(e);
                    // Clear the rejection when the user corrects this field.
                    formOnChanged();
                  }}
                  value={values.title}
                  disabled={isSubmitting}
                />
                {getErrors('title')}
              </FormGroup>

              <FormGroup
                controlId="formControlsEmail"
                validationState={jobValidationErrors.email ? 'error' : null}
                style={{
                  marginBottom: values.email !== '' ? '4px' : '15px',
                }}
              >
                <ControlLabel>Enter email for results</ControlLabel>
                &nbsp;
                <i>(optional)</i>
                <FormControl
                  id="formControlsEmail"
                  name="email"
                  type="email"
                  placeholder="Enter email"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(e);
                    // Clear the rejection when the user corrects this field.
                    formOnChanged();
                  }}
                  value={values.email}
                  disabled={isSubmitting}
                />
                {getErrors('email')}
              </FormGroup>

              <div
                style={{
                  marginBottom: '15px',
                  display: values.email !== '' ? 'block' : 'none',
                }}
              >
                <div style={{ display: 'block', marginTop: '1px' }}>
                  <ControlLabel
                    htmlFor="signup-checkbox"
                    style={{
                      fontWeight: 'normal',
                      marginBottom: '0',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      id="signup-checkbox"
                      name="signup"
                      onChange={handleChange}
                      checked={values.signup}
                      disabled={isSubmitting}
                      style={{ marginRight: '5px' }}
                    />
                    Opt-in to stay up to date with our newsletter
                  </ControlLabel>
                </div>
              </div>

              <SubmitButton isSubmitting={isSubmitting} block />
              <SubmissionErrors
                errors={{
                  non_field_errors: jobValidationErrors.non_field_errors,
                  detail: jobValidationErrors.detail,
                  statusError: jobValidationErrors.statusError,
                  fetchError: jobValidationErrors.fetchError,
                }}
                onDismiss={formOnChanged}
              />
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DeepSEAForm;
